import { useEffect, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import { cn } from '@/components/ui/utils';

export interface LoaderStep {
  label: string;
  /** Roughly how long this step takes. Drives the automatic advance. */
  ms?: number;
}

interface Props {
  steps: LoaderStep[];
  /**
   * How far the real work has actually got, as an index into `steps`. The display never
   * sits behind this — but it never rewinds either, because a step already shown as done
   * must not un-finish.
   */
  reached?: number;
  title?: string;
  /** Shown once the last step has been running a while, on the big corpora. */
  slowNote?: string;
  /**
   * Wait this long before appearing, in ms.
   *
   * For work that is usually quick and occasionally not — opening a page whose data is
   * one request. Showing a staged loader for 150ms is a flash of furniture that reads as
   * jank, and hiding it entirely would leave a slow load looking dead. Nothing is
   * delayed by this: the content renders the moment it arrives, loader shown or not.
   */
  appearAfter?: number;
}

const DEFAULT_STEP_MS = 2200;
const SLOW_AFTER_MS = 8000;

/**
 * A loader that names what it is doing.
 *
 * Some work is one request that fans out — a corpus over its documents, an imported
 * specification over its claims, figures and numerals — and leaves a bare spinner on
 * screen long enough to read as a hang. The API reports only a couple of real checkpoints
 * along the way, so the steps in between advance on a clock: the point is to show the
 * work has stages and is moving through them, and `reached` keeps the clock honest
 * whenever a real checkpoint lands.
 *
 * Shared across features. It shows progress; it never gates it.
 */
export default function StagedLoader({
  steps,
  reached = 0,
  title = 'Loading…',
  slowNote,
  appearAfter = 0,
}: Props) {
  const [index, setIndex] = useState(0);
  const [slow, setSlow] = useState(false);
  const [visible, setVisible] = useState(appearAfter <= 0);
  const last = steps.length - 1;
  // Depending on the step's duration rather than the `steps` array keeps a parent that
  // re-renders (this page polls) from restarting the timer before it ever fires.
  const stepMs = steps[index]?.ms ?? DEFAULT_STEP_MS;

  useEffect(() => {
    if (index >= last) return;
    const timer = window.setTimeout(() => setIndex(i => Math.min(i + 1, last)), stepMs);
    return () => window.clearTimeout(timer);
  }, [index, stepMs, last]);

  useEffect(() => {
    setIndex(i => Math.max(i, Math.min(reached, last)));
  }, [reached, last]);

  // The last step has no successor to advance to, so a long tail there is the only signal
  // left that this is one of the slow ones.
  useEffect(() => {
    if (index < last) {
      setSlow(false);
      return;
    }
    const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => window.clearTimeout(timer);
  }, [index, last]);

  // The clock still runs while hidden, so a loader that does appear appears at the step
  // the elapsed time has actually reached rather than restarting from the first.
  useEffect(() => {
    if (appearAfter <= 0) return;
    const timer = window.setTimeout(() => setVisible(true), appearAfter);
    return () => window.clearTimeout(timer);
  }, [appearAfter]);

  const percent = Math.min(95, ((index + 0.6) / steps.length) * 100);

  if (!visible) return null;

  return (
    <div className="flex justify-center py-20">
      <div className="w-full max-w-sm">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-900">
          <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          {title}
        </div>

        <div className="mt-3 h-1 overflow-hidden rounded-full bg-slate-200">
          <div
            className="h-full rounded-full bg-slate-900 transition-all duration-700 ease-out"
            style={{ width: `${percent}%` }}
          />
        </div>

        <ul className="mt-4 space-y-2">
          {steps.map((step, i) => (
            <li
              key={step.label}
              className={cn(
                'flex items-center gap-2 text-sm transition-colors duration-300',
                i < index && 'text-slate-500',
                i === index && 'text-slate-900',
                i > index && 'text-slate-300'
              )}
            >
              <span className="flex h-4 w-4 shrink-0 items-center justify-center">
                {i < index ? (
                  <Check className="h-4 w-4 text-emerald-600" />
                ) : i === index ? (
                  <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                ) : (
                  <span className="h-1.5 w-1.5 rounded-full bg-slate-300" />
                )}
              </span>
              <span>{step.label}</span>
            </li>
          ))}
        </ul>

        {slow && slowNote && <p className="mt-4 text-xs text-slate-400">{slowNote}</p>}
      </div>
    </div>
  );
}
